import React, { useState, useEffect, useMemo, useRef } from 'react';

import { Modal } from 'flowbite';

import GeneralRiskStatusCreateModal from './GeneralRiskStatusCreateModal';
import GeneralRiskStatusesTable from './GeneralRiskStatusesTable';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import Toast from 'CommonComponents/Toast';

const CREATE_MODAL_ID = 'create-general-risk-status-modal';
const DELETE_MODAL_ID = 'delete-general-risk-status-modal';

export default function GeneralRiskStatusesTab(props) {
    const [statuses, setStatuses] = useState([]);
    const [deletableStatus, setDeletableStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [generalRiskStatusDeleteModalElement, setGeneralRiskStatusDeleteModalElement] = useState(null);
    const [generalRiskStatusCreateModalElement, setGeneralRiskStatusCreateModalElement] = useState(null);

    const generalRiskStatusDeleteModalRef = useRef();
    const generalRiskStatusCreateModalRef = useRef();

    const deleteUrl = useMemo(() => deletableStatus && props.deleteUrl.replace('STATUS_ID', deletableStatus.id), [deletableStatus]);

    useEffect(() => {
        fetchGeneralRiskStatuses();
        generalRiskStatusDeleteModalRef && setGeneralRiskStatusDeleteModalElement(new Modal(generalRiskStatusDeleteModalRef.current, modalOptions(false)));
        generalRiskStatusCreateModalRef && setGeneralRiskStatusCreateModalElement(new Modal(generalRiskStatusCreateModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        deletableStatus && showModal(generalRiskStatusDeleteModalElement);
    }, [deletableStatus]);

    const showModal = (modalElement) => {
        modalElement && modalElement.show();
    };

    // After a create, edit or delete: reload the list and tell an embedding
    // page that the data its status depends on has changed.
    const refetchAfterChange = () => {
        fetchGeneralRiskStatuses();

        if (props.onChanged) {
            props.onChanged();
        }
    };

    const fetchGeneralRiskStatuses = () => {
        setIsLoading(true);
        axios
            .get(props.fetchUrl)
            .then(({ data }) => {
                setStatuses([...data]);
            })
            .catch((e) => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const triggerDeleteStatus = () => {
        setActionInProgress(true);

        axios
            .delete(deleteUrl)
            .then((res) => {
                generalRiskStatusDeleteModalElement && generalRiskStatusDeleteModalElement.hide();
                refetchAfterChange();
                $('#general-risk-status-deleted-toast').toast('show');
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setActionInProgress(false);
            });
    };

    return (
        <div>
            <div className={'mb-2 d-flex justify-content-end'}>
                <Button variant="primary" onClick={() => showModal(generalRiskStatusCreateModalElement)}>
                    {$t('Create Risk Status')}
                </Button>
            </div>

            <GeneralRiskStatusesTable
                isLoading={isLoading}
                statuses={statuses}
                updateUrl={props.updateUrl}
                fetch={refetchAfterChange}
                onDelete={setDeletableStatus}
            />

            <GeneralRiskStatusCreateModal
                modalId={CREATE_MODAL_ID}
                modalRef={generalRiskStatusCreateModalRef}
                modalElement={generalRiskStatusCreateModalElement}
                onCreated={refetchAfterChange}
                storeUrl={props.storeUrl}
            />

            <ConfirmationModal
                successHandler={refetchAfterChange}
                triggerAction={triggerDeleteStatus}
                cleanStates={cleanConfirmationModalStates}
                allowAction={deletableStatus}
                modalId={DELETE_MODAL_ID}
                modalRef={generalRiskStatusDeleteModalRef}
                modalElement={generalRiskStatusDeleteModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                confirmationMsg={$t('Are you sure you want to delete this {{title}}?', { title: deletableStatus?.title })}
                actionCompletedMsg=""
                actionFailedMsg=""
                title={$t('Delete {{title}}', { title: deletableStatus?.title })}
                cancelButtonText={$t('Cancel')}
                primaryButtonText={$t('OK')}
            />

            <Toast id="general-risk-status-update-toast" message={$t('general risk status updated successfully')} />
            <Toast id="general-risk-status-update-failed-toast" message={$t('general risk status update failed')} />
            <Toast id="general-risk-status-deleted-toast" message={$t('general risk status deleted successfully')} />
        </div>
    );
}
